import { useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import DashboardLayout from "./layouts/DashboardLayout";
import LandingPage from "./pages/LandingPage";
import DashboardPage from "./pages/DashboardPage";
import InteractiveExperiencePage from "./pages/InteractiveExperiencePage";
import StaticReportPage from "./pages/StaticReportPage";
import InfographicReportPage from "./pages/InfographicReportPage";
import BuyVsBuildPage from "./pages/BuyVsBuildPage";
import DemoChatPage from "./pages/DemoChatPage";
import QuestionnairePage from "./pages/QuestionnairePage";
import VendorCallDemoPage from "./pages/VendorCallDemoPage";
import AdminPage from "./pages/AdminPage";
import TransparencyPage from "./pages/TransparencyPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import AuthCallbackPage from "./pages/AuthCallbackPage";
import ProtectedRoute from "./components/common/ProtectedRoute";
import AdminRoute from "./components/common/AdminRoute";
import AIAssistantOverlay from "./components/assistant/AIAssistantOverlay";
import { useAuth } from "./contexts/AuthContext";

const isProd = import.meta.env.VITE_APP_ENV === 'production';
const ENV_BANNER_HEIGHT = '36px';

export default function App() {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const isInteractiveReport = location.pathname.startsWith("/reports/experience");

  useEffect(() => {
    if (!isProd) {
      document.documentElement.style.setProperty('--env-banner-height', ENV_BANNER_HEIGHT);
    }
    return () => {
      document.documentElement.style.removeProperty('--env-banner-height');
    };
  }, []);

  return (
    <>
    {/* Environment warning banner for non-production */}
    {!isProd && (
      <div
        style={{
          background: '#ff4400',
          color: '#fff',
          textAlign: 'center',
          padding: '8px 16px',
          fontWeight: 700,
          fontSize: '14px',
          letterSpacing: '0.5px',
          position: 'sticky',
          top: 0,
          zIndex: 9999,
        }}
      >
        ⚠ WARNING: You are using the test system
      </div>
    )}

    {/* Global AI Assistant Overlay — hidden on interactive report for immersion */}
    {isAuthenticated && !isInteractiveReport && <AIAssistantOverlay />}

    <Routes>
      {/* Public landing page */}
      <Route path="/" element={<LandingPage />} />

      {/* Public auth routes */}
      <Route
        path="/login"
        element={
          isAuthenticated ? <Navigate to="/analysis" replace /> : <LoginPage />
        }
      />
      <Route
        path="/register"
        element={
          isAuthenticated ? <Navigate to="/analysis" replace /> : <RegisterPage />
        }
      />
      <Route
        path="/forgot-password"
        element={
          isAuthenticated ? <Navigate to="/analysis" replace /> : <ForgotPasswordPage />
        }
      />
      <Route
        path="/reset-password"
        element={
          isAuthenticated ? <Navigate to="/analysis" replace /> : <ResetPasswordPage />
        }
      />

      {/* OAuth callback route */}
      <Route path="/auth/callback/:provider" element={<AuthCallbackPage />} />

      {/* Public demo route (no auth required) */}
      <Route path="/vendor-call/demo" element={<VendorCallDemoPage />} />

      {/* Fullscreen report views - outside dashboard layout */}
      {/* Demo routes without evaluation ID (use mock data) */}
      <Route
        path="/reports/experience"
        element={
          <ProtectedRoute>
            <InteractiveExperiencePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/static"
        element={
          <ProtectedRoute>
            <StaticReportPage />
          </ProtectedRoute>
        }
      />
      {/* Routes with evaluation ID */}
      <Route
        path="/reports/experience/:evaluationId"
        element={
          <ProtectedRoute>
            <InteractiveExperiencePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/static/:evaluationId"
        element={
          <ProtectedRoute>
            <StaticReportPage />
          </ProtectedRoute>
        }
      />
      {/* Infographic report route */}
      <Route
        path="/reports/infographic/:evaluationId"
        element={
          <ProtectedRoute>
            <InfographicReportPage />
          </ProtectedRoute>
        }
      />

      {/* Strategy / Buy vs Build page (Admin only) */}
      <Route
        path="/strategy"
        element={
          <AdminRoute>
            <BuyVsBuildPage />
          </AdminRoute>
        }
      />

      {/* Demo Chat page - Admin only, fullscreen */}
      <Route
        path="/chat"
        element={
          <AdminRoute>
            <DemoChatPage />
          </AdminRoute>
        }
      />

      {/* Transparency page */}
      <Route
        path="/transparency"
        element={
          <ProtectedRoute>
            <TransparencyPage />
          </ProtectedRoute>
        }
      />

      {/* Protected dashboard routes */}
      <Route
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/analysis" element={<DashboardPage />} />
        <Route path="/vendor-call" element={<QuestionnairePage />} />
        {/* Admin-only routes */}
        <Route
          path="/admin"
          element={<Navigate to="/admin/users" replace />}
        />
        <Route
          path="/admin/users"
          element={
            <AdminRoute>
              <AdminPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/prompts"
          element={
            <AdminRoute>
              <AdminPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/stats"
          element={
            <AdminRoute>
              <AdminPage />
            </AdminRoute>
          }
        />
        {/* Legacy redirect */}
        <Route
          path="/users"
          element={<Navigate to="/admin/users" replace />}
        />
      </Route>

      {/* Redirect unknown routes to home or login */}
      <Route
        path="*"
        element={<Navigate to="/" replace />}
      />
    </Routes>
    </>
  );
}
